import type { ReactNode } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';

import { Icon } from '../icons/Icon';
import type { IconTone } from '../icons/Icon';
import type { IconName } from '../icons/iconNames';
import { Text } from '../primitives/Text';
import { useTheme } from '../theme/ThemeProvider';

export type ListRowProps = {
  title: string;
  titleAccessory?: ReactNode;
  subtitles?: readonly string[];
  children?: ReactNode;
  icon?: IconName;
  iconTone?: IconTone;
  trailing?: ReactNode;
  showChevron?: boolean;
  accentColor?: string;
  selected?: boolean;
  disabled?: boolean;
  onPress?: () => void | Promise<void>;
  accessibilityLabel?: string;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export function ListRow({
  title,
  titleAccessory,
  subtitles,
  children,
  icon,
  iconTone = 'muted',
  trailing,
  showChevron = false,
  accentColor,
  selected = false,
  disabled = false,
  onPress,
  accessibilityLabel,
  style,
  testID,
}: ListRowProps) {
  const theme = useTheme();

  const containerStyle: ViewStyle = {
    backgroundColor: selected ? theme.colors.surfaceAlt : theme.colors.surface,
    borderLeftColor: accentColor ?? 'transparent',
    borderLeftWidth: accentColor !== undefined ? theme.spacing(1) : 0,
    paddingVertical: theme.spacing(3),
    paddingHorizontal: theme.spacing(4),
    gap: theme.spacing(3),
  };

  return (
    <Pressable
      accessibilityRole={onPress !== undefined ? 'button' : undefined}
      accessibilityLabel={accessibilityLabel ?? title}
      aria-disabled={disabled}
      aria-selected={selected}
      disabled={disabled || onPress === undefined}
      onPress={() => void onPress?.()}
      testID={testID}
      style={({ pressed }) => [
        styles.row,
        containerStyle,
        disabled && styles.disabled,
        pressed && !disabled && styles.pressed,
        style,
      ]}
    >
      {icon !== undefined ? <Icon name={icon} size="lg" tone={iconTone} /> : null}

      <View style={styles.body}>
        <View style={[styles.titleLine, { gap: theme.spacing(2) }]}>
          <Text weight="semibold" numberOfLines={2} style={styles.title}>
            {title}
          </Text>
          {titleAccessory}
        </View>
        {subtitles?.map((subtitle) => (
          <Text key={subtitle} variant="caption" numberOfLines={2}>
            {subtitle}
          </Text>
        ))}
        {children}
      </View>

      {trailing}

      {showChevron ? <Icon name="chevron-right" size="sm" tone="muted" /> : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center' },
  body: { flex: 1, gap: 2 },
  titleLine: { flexDirection: 'row', alignItems: 'center' },
  title: { flexShrink: 1 },
  disabled: { opacity: 0.5 },
  pressed: { opacity: 0.7 },
});
